import { auth } from '@/services/firebase/config';
import { notifySessionExpired } from '@/services/api/authTokenManager';

/**
 * Utility function to make authenticated API calls with Firebase JWT token
 */
export async function authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
  try {
    // Get the current user's ID token
    const currentUser = auth?.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    const token = await currentUser.getIdToken();
    
    // Add Authorization header to the request
    const authenticatedOptions: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    };

    const response = await fetch(url, authenticatedOptions);

    // Intercept 401 globally — trigger sign-out before caller sees the response
    if (response.status === 401) {
      notifySessionExpired();
    }

    return response;
  } catch (error) {
    console.error('Error making authenticated request:', error);
    throw error;
  }
}

/**
 * Utility function to make authenticated API calls that return JSON
 */
export async function authenticatedFetchJSON<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await authenticatedFetch(url, options);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
  }
  
  return response.json() as T;
}